import React from "react"
import { observer } from "mobx-react-lite"
import clsx from "clsx"

import { useStore } from "@store/index"
import { SolutionData } from "@framework/types/solution"
import { getCategorySolutions } from "@utils/product-solution"

import SolutionCard from "./SolutionCard"
import ContextMenu from "./SolutionContextMenu"
import CopilotLandingCard from "./CopilotLandingCard"

import styles from "./AvailableSolutions.module.sass"

/** Homepage landing AC: hide these solution cards (and the create-solution card). */
const HIDDEN_HOME_SOLUTION =
  /^(product comparison|product recommendation)$/i

const isLabWizardSolution = (item: SolutionData) =>
  /^lab\s*wizard$/i.test(String(item.name || "").trim())

type SolutionsCardContainerProps = {
  className?: string
  onLaunch?: (option: SolutionData) => void
  onCreate?: () => void
}

const SolutionsCardContainer: React.FC<SolutionsCardContainerProps> = observer(
  ({ className, onLaunch }) => {
    const { solutionsStore, restrictionsStore: access } = useStore()

    const { solutions, category } = solutionsStore

    if (!category) return <></>

    let availableSolutions = getCategorySolutions(
      category,
      solutions || []
    ).filter((item) => !HIDDEN_HOME_SOLUTION.test(String(item.name || "").trim()))

    // LAB-115: homepage shows only the two LabWizard cards when Copilot is enabled.
    if (access.showCopilot) {
      availableSolutions = availableSolutions.filter(isLabWizardSolution)
    }

    const orderedSolutions = [...availableSolutions].sort((a, b) => {
      const aLab = isLabWizardSolution(a) ? 0 : 1
      const bLab = isLabWizardSolution(b) ? 0 : 1
      return aLab - bLab
    })

    const cards: React.ReactNode[] = []
    let insertedCopilotCard = false

    orderedSolutions.forEach((item) => {
      const displaySolution =
        access.showCopilot && isLabWizardSolution(item)
          ? {
              ...item,
              name: "LabWizard (Discontinuing Soon)",
            }
          : item

      cards.push(
        <SolutionCard
          onClick={onLaunch}
          solution={displaySolution}
          contextMenuSlot={<ContextMenu solution={item} />}
          key={item.id}
        />
      )

      if (
        access.showCopilot &&
        !insertedCopilotCard &&
        isLabWizardSolution(item)
      ) {
        cards.push(<CopilotLandingCard key="labwizard-copilot-new" />)
        insertedCopilotCard = true
      }
    })

    if (access.showCopilot && !insertedCopilotCard) {
      cards.push(<CopilotLandingCard key="labwizard-copilot-new" />)
    }

    return (
      <div className={clsx(styles.solutionCardContainer, className)}>
        {cards}
      </div>
    )
  }
)

export default SolutionsCardContainer
